import { DialogRef } from '@angular/cdk/dialog';
import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, OnDestroy, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormControl, FormsModule, ReactiveFormsModule, ValidatorFn } from '@angular/forms';
import { DraggableDialogHeaderComponent } from '@dialog';
import { ServiceUpgradeInjectorService } from '@shims';
import { injectDispatch } from '@ngrx/signals/events';
import { visitBridgePageEvents } from '@visit-bridge';
import { MessagingStore } from '../../../../../data-access/src/index';
import { Message } from '../../../../../util/src/index';
import { MessageBubbleComponent, SenderType } from '../../../../../components/ui/src/index';
import { MessageContentPipe } from '../../../../../components/pipes/src/index';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'messaging-chat-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    DraggableDialogHeaderComponent,
    MessageContentPipe,
    MessageBubbleComponent,
  ],
  templateUrl: './chat-dialog.component.html',
  styleUrls: [
    '../../../../../../ui-components/src/lib/_styles/abstracts/_colors.scss',
    '../../../../../../ui-components/src/lib/_styles/abstracts/_typography.scss',
    '../../../../../../ui-components/src/lib/_styles/abstracts/_spacing.scss',
    './chat-dialog.component.scss',
  ],
  providers: [],
})
export class ChatDialogComponent implements OnInit, OnDestroy {
  private readonly dialogRef = inject(DialogRef);
  protected readonly store = inject(MessagingStore);
  private readonly upgradeService = inject(ServiceUpgradeInjectorService);
  private readonly $rootScope = this.upgradeService.getAngularJsRootScope();
  private readonly $state = this.upgradeService.getAngularJsUiState();
  private readonly dispatch = injectDispatch(visitBridgePageEvents);
  private deregisterNavigate?: () => void;
  messageForm = new FormControl<string>('');

  protected message = '';
  protected hideSystemMessages = false;
  protected readonly sending = signal(false);

  protected readonly messages = computed(() => this.store.patientMessages());
  protected readonly categories = computed(() => this.store.categories());
  protected readonly hasMore = computed(() => this.store.hasMore());
  protected readonly isLoading = computed(() => this.store.isLoading());
  protected readonly patientSummary = computed(() => {
    const patient = this.store.patientSummaryData();
    return `${patient?.firstname || ''} ${patient?.lastname || ''} - ${patient?.state?.code || ''}`;
  });
  readonly patientId = computed(() => this.store.activePatientId() || 0);

  constructor() {
    effect(() => {
      const patient = this.store.patientSummaryData();
      if (patient) {
        const name = patient.firstname;
        const greeting = `Hello ${name},\n\n`;
        this.messageForm.setValue(greeting);
        this.messageForm.addValidators(this.unchangedValidator(greeting));
        this.messageForm.updateValueAndValidity();
      }
    });
    effect(() => {
      const patientId = this.store.activePatientId();
      if (patientId) {
        this.store.openPatientMessages(patientId);
      }
    });
  }

  ngOnInit() {
    this.dispatch.activePatientRequested();
    this.store.startPolling();
    this.deregisterNavigate = this.$rootScope.$on(
      'dialog:navigate',
      (_event: unknown, navData: { state: string; params: Record<string, unknown> }) => {
        this.dialogRef.close();
        this.$state.go(navData.state, navData.params);
      },
    );
  }

  protected sendMessage() {
    if (this.messageForm.invalid) return;
    this.store.sendMessage({
      patientId: this.patientId(),
      content: this.messageForm.value as string,
      entryType: 'message',
    });
    this.messageForm.reset();
  }

  protected markAsRead() {
    this.store.markPatientMessagesAsRead({ patientId: this.patientId() });
  }

  protected updateCategory(messageId: number, categoryId: number) {
    this.store.updateCategory({ messageId, categoryId });
  }

  protected toggleReadStatus(messageId: number, readAt: string | null) {
    this.store.updateReadStatus({ messageId, readAt: readAt ? null : 'now' });
  }

  protected isSystemMessage(msg: Message): boolean {
    return msg.from.id === -1 || msg.from.firstname === null;
  }

  protected shouldHide(msg: Message): boolean {
    return this.hideSystemMessages && this.isSystemMessage(msg);
  }

  protected getSenderType(msg: Message): SenderType {
    if (msg.from.id === -1 || msg.from.firstname === null) return 'system';
    if (msg.from.id === this.patientId()) return 'patient';
    return 'doctor';
  }

  unchangedValidator(initial: string): ValidatorFn {
    return (control: AbstractControl) => (control.value === initial ? { unchanged: true } : null);
  }

  close() {
    this.dialogRef.close();
  }

  ngOnDestroy() {
    this.deregisterNavigate?.();
  }
}
